import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Eye, Info, Check } from "lucide-react";
import { toast } from "sonner";

export default function CardSheet() {
  const { t } = useI18n();
  const { hasRole } = useAuth();
  const canBatch = hasRole("super_admin", "direction", "administration");
  const [mode, setMode] = useState("group");
  const [groups, setGroups] = useState([]);
  const [levels, setLevels] = useState([]);
  const [students, setStudents] = useState([]);
  const [group, setGroup] = useState("");
  const [level, setLevel] = useState("");
  const [ids, setIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [onlyUnprinted, setOnlyUnprinted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);

  useEffect(() => {
    api.get("/school/groups").then((r) => setGroups(r.data.items || r.data)).catch(() => {});
    api.get("/school/levels").then((r) => setLevels(r.data.items || r.data)).catch(() => {});
    api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
  }, []);

  const body = () => ({ ...(mode === "group" ? { group_id: group } : mode === "level" ? { level_id: level } : { student_ids: ids }), only_unprinted: onlyUnprinted });
  const canRun = (mode === "group" && group) || (mode === "level" && level) || (mode === "manual" && ids.length);

  const doPreview = async () => {
    try { const r = await api.post("/people/cards/sheet/preview", body()); setPreview(r.data); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post("/people/cards/sheet", body(), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "planche_cartes_A4.pdf"; a.click();
      URL.revokeObjectURL(url);
      const pv = await api.post("/people/cards/sheet/preview", body());
      setLastBatch((pv.data.students || []).filter((s) => s.has_card).map((s) => s.card_id));
      toast.success(t("generateSheet"));
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur";
      toast.error(typeof msg === "string" ? msg : "Erreur");
    } finally { setBusy(false); }
  };
  const markBatch = async () => {
    if (!lastBatch?.length || !window.confirm(t("markBatchConfirm"))) return;
    try { const r = await api.post("/people/cards/mark-batch-printed", { card_ids: lastBatch }); toast.success(`${r.data.marked} ✓`); setLastBatch(null); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const toggleId = (id) => setIds((x) => x.includes(id) ? x.filter((i) => i !== id) : [...x, id]);

  return (
    <div data-testid="card-sheet" className="max-w-3xl">
      <h1 className="text-2xl font-display font-bold text-slate-900 mb-1">{t("cardPrintSheet")}</h1>
      <div className="flex items-start gap-2 text-sm text-slate-500 mb-5"><Info className="h-4 w-4 mt-0.5 shrink-0" />{t("sheetHint")}</div>

      <Card className="border-slate-200 p-5 mb-4">
        <Tabs value={mode} onValueChange={(v) => { setMode(v); setPreview(null); }}>
          <TabsList data-testid="sheet-mode-tabs">
            <TabsTrigger value="group" data-testid="mode-group">{t("byGroup")}</TabsTrigger>
            <TabsTrigger value="level" data-testid="mode-level">{t("byLevel")}</TabsTrigger>
            <TabsTrigger value="manual" data-testid="mode-manual">{t("byManual")}</TabsTrigger>
          </TabsList>
          <TabsContent value="group" className="pt-4">
            <Select value={group} onValueChange={(v) => { setGroup(v); setPreview(null); }}>
              <SelectTrigger className="w-72" data-testid="sheet-group-select"><SelectValue placeholder={t("byGroup")} /></SelectTrigger>
              <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.group_name} {g.group_code ? `(${g.group_code})` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </TabsContent>
          <TabsContent value="level" className="pt-4">
            <Select value={level} onValueChange={(v) => { setLevel(v); setPreview(null); }}>
              <SelectTrigger className="w-72" data-testid="sheet-level-select"><SelectValue placeholder={t("byLevel")} /></SelectTrigger>
              <SelectContent>{levels.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </TabsContent>
          <TabsContent value="manual" className="pt-4">
            <div className="max-h-64 overflow-auto rounded-lg border border-slate-100 divide-y" data-testid="sheet-manual-list">
              {students.map((s) => (
                <label key={s.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                  <Checkbox checked={ids.includes(s.id)} onCheckedChange={() => toggleId(s.id)} data-testid={`sheet-student-${s.id}`} />
                  {s.name}
                </label>
              ))}
            </div>
            <div className="text-xs text-slate-400 mt-1">{ids.length} {t("selectedCount")}</div>
          </TabsContent>
        </Tabs>
      </Card>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <label className="flex items-center gap-2 text-sm text-slate-600 rounded-lg border border-slate-100 px-3 py-2">
          <Checkbox checked={onlyUnprinted} onCheckedChange={(v) => { setOnlyUnprinted(!!v); setPreview(null); }} data-testid="only-unprinted" />
          {t("onlyUnprinted")}
        </label>
        <Button variant="outline" className="gap-2" data-testid="sheet-preview-btn" disabled={!canRun} onClick={doPreview}><Eye className="h-4 w-4" />{t("previewSelection")}</Button>
        <Button className="gap-2" data-testid="sheet-generate-btn" disabled={!canRun || busy} onClick={generate}><Printer className="h-4 w-4" />{t("generateSheet")}</Button>
        {canBatch && lastBatch && lastBatch.length > 0 && (
          <Button variant="secondary" className="gap-2" data-testid="mark-batch-btn" onClick={markBatch}><Check className="h-4 w-4" />{t("markBatchPrinted")}</Button>
        )}
      </div>

      {preview && (
        <Card className="border-slate-200 p-5" data-testid="sheet-preview">
          <div className="flex items-center gap-4 mb-3">
            <Badge className="bg-primary/10 text-primary border-0 text-sm px-3 py-1" data-testid="sheet-printable-count">{preview.printable} {t("printableCards")}</Badge>
            <span className="text-xs text-slate-400">{preview.total} {t("selectedCount")}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {preview.students.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm" data-testid="sheet-preview-row">
                <span className="text-slate-700">{s.name}</span>
                <span className="flex items-center gap-2">
                  {s.photo_validated && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">photo</Badge>}
                  <Badge className={`border-0 text-xs ${s.has_card ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-400"}`}>{s.has_card ? s.card_uid : "—"}</Badge>
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
